import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Newspaper, BookOpen, FileText, Calculator, MessageCircleQuestion, Crown, Search, PenLine, UserCheck, Send, RotateCcw, ShieldCheck, Mail, Handshake } from "lucide-react";
import { Seo } from "../components/site/Seo";
import { BRAND } from "../lib/brand";

const COUNTRIES = ["Bosna i Hercegovina", "Srbija", "Hrvatska", "Crna Gora", "Kosovo", "Sjeverna Makedonija"];
const OFFER = [
  { icon: Newspaper, t: "Vijesti koje se tiču tebe", d: "Promjene zakona, rokovi i odluke institucija – objašnjeno šta znače u praksi.", to: "/najnovije" },
  { icon: BookOpen, t: "Vodiči korak po korak", d: "Boravak, priznavanje diploma, prvi koraci u Njemačkoj i druge teme, uz službene izvore.", to: "/vodici/prvi-koraci" },
  { icon: FileText, t: "Službeni formulari", d: "Pregled formulara s linkom direktno na stranicu nadležne institucije.", to: "/formulari" },
  { icon: Calculator, t: "Alati i kalkulatori", d: "Orijentacioni izračuni za plate i druge svakodnevne teme.", to: "/alati" },
  { icon: MessageCircleQuestion, t: "Pitaj Balkan AI", d: "Odgovori na pitanja uz izvore, s jasnom napomenom da ne zamjenjuju savjetovanje.", to: "/pitaj-balkan-ai" },
  { icon: Crown, t: "Premium vodiči", d: "Detaljniji vodiči i dodatni alati. Vijesti i važne javne informacije ostaju besplatne.", to: "/premium" },
];
const STEPS = [
  { icon: Search, t: "Izvori", d: "Pratimo službene stranice institucija, zakonodavca i provjerene medije." },
  { icon: PenLine, t: "Istraživanje i nacrt", d: "Tvrdnje povezujemo s izvorima. Dio nacrta pripremaju AI alati." },
  { icon: UserCheck, t: "Ljudska provjera", d: "Svaki tekst prije objave pregleda i odobri član redakcije." },
  { icon: Send, t: "Objava", d: "Uz tekst navodimo izvore i datum ažuriranja." },
  { icon: RotateCcw, t: "Ispravke", d: "Greške ispravljamo transparentno i bilježimo ih." },
];

const Reveal = ({ children, i = 0, className = "" }) => (
  <motion.div initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.45, delay: i * 0.06 }} className={className}>{children}</motion.div>
);

const Kicker = ({ children }) => <p className="text-xs font-bold uppercase tracking-[0.18em] text-pulse">{children}</p>;
const H2 = ({ children, id }) => <h2 id={id} className="mt-2 font-display text-3xl font-semibold leading-tight text-ink sm:text-4xl">{children}</h2>;

const Hero = () => (
  <section className="border-b border-border bg-ink text-white" data-testid="about-hero">
    <div className="container-news grid gap-10 py-16 sm:py-24 lg:grid-cols-[1.4fr_1fr] lg:items-end">
      <Reveal>
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-white/60">O nama</p>
        <h1 className="mt-4 font-display text-4xl font-semibold leading-[1.05] sm:text-5xl lg:text-6xl">{BRAND.slogan}</h1>
        <p className="mt-6 max-w-xl text-base leading-relaxed text-white/80 sm:text-lg">BalkanPuls.de je digitalni portal na našem jeziku za ljude s Balkana koji žive, rade ili planiraju život u Njemačkoj. Složene teme objašnjavamo jasno, uz izvore i bez senzacionalizma.</p>
      </Reveal>
      <Reveal i={2}>
        <ul className="flex flex-wrap gap-2" data-testid="about-countries">
          {COUNTRIES.map((c) => <li key={c} className="rounded-full border border-white/20 px-3 py-1.5 text-[13px] text-white/85">{c}</li>)}
        </ul>
      </Reveal>
    </div>
  </section>
);

const Offer = () => (
  <section className="container-news py-16 sm:py-20" aria-labelledby="about-offer">
    <Kicker>Šta nudimo</Kicker><H2 id="about-offer">Praktične informacije za svakodnevicu</H2>
    <div className="mt-10 grid gap-px overflow-hidden rounded-lg border border-border bg-border sm:grid-cols-2 lg:grid-cols-3">
      {OFFER.map((o, i) => (
        <Reveal key={o.t} i={i} className="bg-background">
          <Link to={o.to} className="group block h-full p-6 transition-colors duration-200 hover:bg-muted" data-testid={`about-offer-${i}`}>
            <o.icon className="h-6 w-6 text-pulse transition-transform duration-200 group-hover:-translate-y-0.5" />
            <h3 className="mt-4 font-display text-xl font-semibold text-ink">{o.t}</h3>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{o.d}</p>
          </Link>
        </Reveal>
      ))}
    </div>
  </section>
);

const Process = () => (
  <section className="border-y border-border bg-muted/50" aria-labelledby="about-process" data-testid="about-process">
    <div className="container-news py-16 sm:py-20">
      <Kicker>Kako radimo</Kicker><H2 id="about-process">Od izvora do objave</H2>
      <ol className="mt-10 grid gap-8 md:grid-cols-5">
        {STEPS.map((s, i) => (
          <Reveal key={s.t} i={i}>
            <li className="border-t-2 border-ink pt-4">
              <span className="font-mono text-xs text-muted-foreground">0{i + 1}</span>
              <s.icon className="mt-3 h-5 w-5 text-pulse" />
              <h3 className="mt-3 font-semibold text-ink">{s.t}</h3>
              <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">{s.d}</p>
            </li>
          </Reveal>
        ))}
      </ol>
    </div>
  </section>
);

const Principles = () => (
  <section className="container-news grid gap-12 py-16 sm:py-20 lg:grid-cols-2" data-testid="about-principles">
    <Reveal>
      <Kicker>AI i ljudi</Kicker><H2>Transparentno o umjetnoj inteligenciji</H2>
      <p className="mt-5 leading-relaxed">AI alate koristimo za praćenje izvora, istraživanje i prve nacrte. AI nikada sam ne objavljuje: svaki tekst pregleda i odobri čovjek. Tekstovi nastali uz pomoć AI-a su označeni.</p>
      <p className="mt-4 leading-relaxed">Naši tekstovi i vodiči su opće informacije. Ne zamjenjuju pravni, poreski ni drugi savjet za tvoj slučaj – za to se obrati nadležnoj instituciji ili savjetovalištu.</p>
    </Reveal>
    <Reveal i={2}>
      <Kicker>Nezavisnost</Kicker><H2>Jasno odvojeno</H2>
      <p className="mt-5 leading-relaxed">Oglasi i partnerski sadržaji su uvijek označeni i odvojeni od uredničkog sadržaja. Činjenice odvajamo od analize i komentara.</p>
      <div className="mt-6 flex flex-wrap gap-3 text-sm">
        <Link to="/urednicka-politika" className="inline-flex items-center gap-2 rounded-full border border-border px-4 py-2 font-medium transition-colors hover:bg-muted" data-testid="about-policy-link"><ShieldCheck className="h-4 w-4" /> Urednička politika</Link>
        <Link to="/ispravke" className="inline-flex items-center gap-2 rounded-full border border-border px-4 py-2 font-medium transition-colors hover:bg-muted" data-testid="about-corrections-link"><RotateCcw className="h-4 w-4" /> Ispravke</Link>
      </div>
    </Reveal>
  </section>
);

const Contact = () => (
  <section className="container-news pb-20" data-testid="about-contact">
    <div className="grid gap-8 rounded-lg bg-ink p-8 text-white sm:p-12 md:grid-cols-[1.3fr_1fr] md:items-center">
      <div>
        <h2 className="font-display text-3xl font-semibold">Imaš temu, pitanje ili prijedlog?</h2>
        <p className="mt-3 text-white/75">Piši nam. Prijave grešaka, prijedloge tema i upite za saradnju čitamo u redakciji.</p>
      </div>
      <div className="flex flex-col gap-3 sm:flex-row md:flex-col">
        <a href={`mailto:${BRAND.email}`} className="inline-flex items-center justify-center gap-2 rounded-full bg-white px-5 py-3 font-semibold text-ink transition-transform duration-200 hover:-translate-y-0.5" data-testid="about-email-link"><Mail className="h-4 w-4" /> {BRAND.email}</a>
        <Link to="/postani-partner" className="inline-flex items-center justify-center gap-2 rounded-full border border-white/30 px-5 py-3 font-semibold transition-colors hover:bg-white/10" data-testid="about-partner-link"><Handshake className="h-4 w-4" /> Saradnja i partnerstvo</Link>
      </div>
    </div>
  </section>
);

export default function About() {
  return (
    <div data-testid="info-page-o-nama">
      <Seo title="O nama" description="BalkanPuls.de – informacije na našem jeziku za ljude s Balkana u Njemačkoj: vijesti, vodiči, formulari i alati uz službene izvore." />
      <Hero /><Offer /><Process /><Principles /><Contact />
    </div>
  );
}
